import Button from '@/components/Button'
import Layout, { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { useAi } from '@/context/ai'
import { useAuth } from '@/context/auth'
import { setUseValidEmail } from '@/lib/reminders'
import Field from '@/widgets/Field'
import Modal from '@/widgets/Modal'
import { ChangeEvent, useState } from 'react'

interface Props {
  changeEvent?: ChangeEvent<HTMLInputElement>
  reminderId?: string
  isOpen: boolean
  close: () => void
}

const RemindersEmailModal = (props: Props): JSX.Element => {
  const { dispatch, subscribeReminder } = useAi()
  const authState = useAuth()
  const [email, setEmail] = useState(authState.state.user?.email)
  const [loading, setLoading] = useState(false)

  const [errorMessages, setErrorMessages] = useState<string[] | undefined>()

  async function onConfirm(): Promise<void> {
    if (!props.changeEvent || !props.reminderId) return

    if (!email) {
      setErrorMessages(['Email is required'])
      return
    }

    setLoading(true)
    const results = await setUseValidEmail({ email })
    setLoading(false)

    if (results.success) {
      dispatch({
        type: 'UPDATE',
        payload: { showRemindersEmailPopup: false },
      })

      await subscribeReminder(props.reminderId)

      props.changeEvent.target.checked = true
      props.close()
    } else {
      switch (results.message) {
        case 'EMAIL_ALREADY_EXISTS':
          setErrorMessages([
            "The email you've entered is already associated with another user account, kindly provide different email.",
          ])
          break

        case 'VALIDATION_ERROR':
          setErrorMessages(['Please check the email'])
          break
      }
    }
  }

  function onClose(): void {
    if (props.changeEvent) {
      props.changeEvent.target.checked = false
    }
    props.close()
  }

  function handleOnChange(event: ChangeEvent<HTMLInputElement>): void {
    setErrorMessages(undefined)
    setEmail(event.target.value)
  }

  return (
    <Modal isOpen={props.isOpen} isClosable={false}>
      <Layout className="px-4" spacing="lg">
        <Stack y>
          <Text size="md">Please confirm your contact information to receive these reminders</Text>
          <Stack x>
            <Field id="email" label="Email" error={!!errorMessages?.length} errorMessages={errorMessages}>
              <input type="text" name="email" defaultValue={email} onChange={handleOnChange} />
            </Field>
          </Stack>
          <Stack x spaceBetweenX="sm" justifyContent="center">
            <Button disabled={loading} loading={loading} label="Confirm" theme="secondary" onClick={onConfirm} />
            <Button label="Cancel" theme="secondaryOutline" onClick={onClose} />
          </Stack>
        </Stack>
      </Layout>
    </Modal>
  )
}

export default RemindersEmailModal
